/**
 * Sheet „Privaten Bereich anlegen" (`private-area-sheet`, Spec 05b, „Privaten
 * Bereich anlegen"). Ein einziges Pflichtfeld, vorbelegt mit „Privat".
 *
 * Kein „und noch eins": Anders als bei Grundstück/Gebäude/Einheit gibt es hier
 * nur den primären Knopf — „man legt einen privaten Bereich an, nicht fünf"
 * (Spec). Navigiert nach dem Anlegen zum neuen Bereich, dieselbe Form wie
 * `PropertySheet`.
 */

import { useNavigate } from '@tanstack/react-router'
import { useRef, useState } from 'react'
import type { SubmitEvent } from 'react'

import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { ApiError } from '@/lib/api'
import { useCreatePrivateArea } from '@/features/objects/api'
import { FIELD_CLASS, FormField } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'

interface PrivateAreaSheetProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
}

const DEFAULT_LABEL = 'Privat'

export function PrivateAreaSheet({ open, onOpenChange }: PrivateAreaSheetProps) {
  const navigate = useNavigate()
  const createMutation = useCreatePrivateArea()
  const labelRef = useRef<HTMLInputElement>(null)

  const [label, setLabel] = useState(DEFAULT_LABEL)
  const [fieldError, setFieldError] = useState<FieldError | undefined>(undefined)
  const [generalError, setGeneralError] = useState<string | undefined>(undefined)

  // Jedes Öffnen beginnt frisch — dieselbe Technik wie in `PropertySheet`
  // (React-Muster „Adjusting state when a prop changes", während des
  // Renderns statt in einem Effekt).
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setLabel(DEFAULT_LABEL)
      setFieldError(undefined)
      setGeneralError(undefined)
    }
  }

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    setFieldError(undefined)
    setGeneralError(undefined)

    try {
      const created = await createMutation.mutateAsync({ label })
      onOpenChange(false)
      await navigate({
        to: '/objekt/$entityId',
        params: { entityId: created.id },
        search: { tab: undefined },
      })
    } catch (error) {
      if (error instanceof ApiError && error.field !== null) {
        setFieldError({ field: error.field, message: error.message })
      } else if (error instanceof ApiError) {
        setGeneralError(error.message)
      } else {
        setGeneralError('Die Verbindung zum Server ist unterbrochen.')
      }
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          labelRef.current?.focus()
          labelRef.current?.select()
        }}
        className="data-[side=right]:!w-full data-[side=right]:sm:!max-w-none md:data-[side=right]:!w-[420px] md:data-[side=right]:!max-w-[420px]"
      >
        <form data-testid="private-area-sheet" onSubmit={(event) => void handleSubmit(event)} className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle className="text-section font-(--font-weight-section) text-text">
              Privaten Bereich anlegen
            </SheetTitle>
          </SheetHeader>

          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
            <FormField
              ref={labelRef}
              label="Name"
              testId="private-area-label"
              apiField="label"
              errorPrefix="private-area"
              required
              value={label}
              onChange={setLabel}
              error={fieldError}
            />
          </div>

          <SheetFooter>
            {generalError !== undefined && (
              <p data-testid="private-area-error" role="alert" className="text-label text-status-overdue">
                {generalError}
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                data-testid="private-area-cancel"
                onClick={() => {
                  onOpenChange(false)
                }}
                className={`text-text px-4 ${FIELD_CLASS}`}
              >
                Abbrechen
              </button>
              <button
                type="submit"
                data-testid="private-area-save"
                disabled={createMutation.isPending}
                aria-busy={createMutation.isPending}
                className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
              >
                Anlegen
              </button>
            </div>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
